/**
 * TypedFieldsEditor — structured editor for typed entity columns.
 *
 * Responsibility: Edit shareCapital/parValue (amount+currency), typed dates,
 * fiscalYearEnd (month+day), duration end + indefinite flag, and notes.
 * All mutations go to PATCH /entities/{id}/typed-fields.
 *
 * Comment is required for every save (critical-adjacent pattern).
 *
 * Query invalidation: ["entity", entityId] + ["changelog-recent"]
 */

import { useState, useMemo, useId } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { X } from "lucide-react";
import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";
import { cn } from "../lib/cn";
import { formatAmount2, toAmountInput } from "../lib/formatNumber";
import { patchTypedFields } from "../lib/dataFetchers";
import { extractApiError } from "../lib/api";
import { Term } from "./ui/Term";
import { Tooltip } from "./ui/Tooltip";
import { SaveButtonWithHint } from "./ui/SaveButtonWithHint";
import { Button } from "./ui/Button";
import { FieldActions } from "./FieldActions";
import { FieldEditHistory } from "./FieldEditHistory";
import { FieldHistoryPopover } from "./FieldHistoryPopover";
import { useFieldHasHistory } from "../lib/EntityHistoryFieldsContext";
import type { Entity, TypedEntityFields } from "../lib/types";
import { ANCHOR_FIELDS } from "../lib/types";

// ─── Currency options ─────────────────────────────────────────────────────────

const COMMON_CURRENCIES = [
  "EUR", "USD", "GBP", "CHF", "JPY", "CAD", "AUD", "SGD",
  "MAD", "AED", "INR", "MXN", "BRL", "IDR", "KES", "ZAR", "MYR",
];

// ─── Month names ──────────────────────────────────────────────────────────────

const MONTHS = [
  "jan", "feb", "mar", "apr", "may", "jun",
  "jul", "aug", "sep", "oct", "nov", "dec",
];

// ─── Field row wrapper ────────────────────────────────────────────────────────

function FieldRow({ children, className }: { children: React.ReactNode; className?: string }): JSX.Element {
  return (
    <div className={cn("flex items-center gap-[14px] py-2 border-b border-border-2 last:border-b-0 group", className)}>
      {children}
    </div>
  );
}

function FieldLabel({ children }: { children: React.ReactNode }): JSX.Element {
  return (
    <span className="w-[148px] shrink-0 text-[13.5px] font-semibold text-text">
      {children}
    </span>
  );
}

// ─── Inline date picker button ────────────────────────────────────────────────

interface DatePickerInputProps {
  value: string | null;
  onChange: (iso: string | null) => void;
  placeholder: string;
  disabled?: boolean;
  ariaLabel?: string;
}

function DatePickerInput({ value, onChange, placeholder, disabled, ariaLabel }: DatePickerInputProps): JSX.Element {
  // Native date input — reliable, consistent, and mobile-friendly (iOS picker).
  return (
    <input
      type="date"
      disabled={disabled}
      aria-label={ariaLabel}
      title={placeholder}
      value={value ?? ""}
      onChange={(e) => onChange(e.target.value || null)}
      className={cn(
        "min-h-[44px] min-w-[160px] px-3 rounded-lg border border-border bg-surface-2 text-base text-left",
        "focus:bg-surface focus:border-accent focus:ring-2 focus:ring-accent/14 focus:outline-none",
        "disabled:opacity-50 disabled:cursor-not-allowed",
        value ? "text-text font-normal" : "text-dim",
      )}
    />
  );
}

// ─── Money field pair ─────────────────────────────────────────────────────────

interface MoneyFieldProps {
  amountKey: "shareCapitalAmount" | "parValueAmount";
  currencyKey: "shareCapitalCurrency" | "parValueCurrency";
  entity: Entity;
  canEdit: boolean;
  label: string;
  amountLabel: string;
  currencyLabel: string;
  entityId: string;
}

function MoneyField({
  amountKey,
  currencyKey,
  entity,
  canEdit,
  label,
  amountLabel,
  currencyLabel,
  entityId,
}: MoneyFieldProps): JSX.Element {
  const { t } = useTranslation("typed_fields");
  const { t: tAudit } = useTranslation("audit");
  const qc = useQueryClient();
  const hasHistoryAmount = useFieldHasHistory(amountKey);
  const hasHistoryCurrency = useFieldHasHistory(currencyKey);
  const hasHistory = hasHistoryAmount || hasHistoryCurrency;
  const uid = useId();
  const [editing, setEditing] = useState(false);
  const [amount, setAmount] = useState<string>(toAmountInput(entity[amountKey]));
  const [currency, setCurrency] = useState<string>(entity[currencyKey] ?? "EUR");
  const [initialAmount, setInitialAmount] = useState<string>("");
  const [initialCurrency, setInitialCurrency] = useState<string>("");

  const amountId = `${uid}-amount`;
  const currencyId = `${uid}-currency`;

  const isDirty = useMemo(
    () => amount !== initialAmount || currency !== initialCurrency,
    [amount, currency, initialAmount, initialCurrency],
  );

  const displayAmount = entity[amountKey];
  const displayCurrency = entity[currencyKey];
  const formattedAmount = formatAmount2(displayAmount);

  const mutation = useMutation({
    mutationFn: () => {
      const payload: TypedEntityFields = {
        [amountKey]: amount !== "" ? parseFloat(amount) : null,
        [currencyKey]: currency || null,
        comment: null,
      };
      return patchTypedFields(entityId, payload);
    },
    onSuccess: () => {
      toast.success(t("save_success"));
      void qc.invalidateQueries({ queryKey: ["entity", entityId] });
      void qc.invalidateQueries({ queryKey: ["entities"] });
      void qc.invalidateQueries({ queryKey: ["renewals"] });
      void qc.invalidateQueries({ queryKey: ["changelog-recent"] });
      setEditing(false);
    },
    onError: (err) => toast.error(extractApiError(err) || t("save_error")),
  });

  if (!editing) {
    return (
      <FieldRow>
        <FieldLabel>{label}</FieldLabel>
        <div className="flex items-center gap-2 flex-1 min-w-0">
          <span className={cn("text-[14px]", formattedAmount != null && displayCurrency ? "font-normal text-text" : "font-normal text-dim")}>
            {formattedAmount != null && displayCurrency ? (
              `${displayCurrency} ${formattedAmount}`
            ) : "—"}
          </span>
          <FieldActions
            fieldLabel={label}
            canEdit={canEdit}
            showClock={hasHistory}
            clockTrigger={hasHistory ? (
              <FieldHistoryPopover
                entityId={entityId}
                field={amountKey}
                triggerAriaLabel={tAudit("open_field_history", { field: label })}
              />
            ) : undefined}
            onEdit={() => {
              const a = toAmountInput(entity[amountKey]);
              const c = entity[currencyKey] ?? "EUR";
              setAmount(a);
              setCurrency(c);
              setInitialAmount(a);
              setInitialCurrency(c);
              setEditing(true);
            }}
            className="ml-auto"
          />
        </div>
      </FieldRow>
    );
  }

  return (
    <div className="py-3 border-b border-border-2 last:border-b-0 space-y-3">
      <FieldLabel>{label}</FieldLabel>
      <div className="flex flex-wrap gap-3">
        <div>
          <label htmlFor={amountId} className="block text-[10px] font-bold tracking-[.05em] uppercase text-dim mb-1.5">{amountLabel}</label>
          <input
            id={amountId}
            type="number"
            min={0}
            step="0.01"
            value={amount}
            // eslint-disable-next-line jsx-a11y/no-autofocus
            autoFocus
            onChange={(e) => setAmount(e.target.value)}
            placeholder={t("amount_placeholder")}
            className={cn(
              "h-9 w-36 px-3 rounded-lg border border-border bg-surface-2 text-[13.5px]",
              "focus:bg-surface focus:border-accent focus:ring-2 focus:ring-accent/14 focus:outline-none",
            )}
          />
        </div>
        <div>
          <label htmlFor={currencyId} className="block text-[10px] font-bold tracking-[.05em] uppercase text-dim mb-1.5">{currencyLabel}</label>
          <select
            id={currencyId}
            value={currency}
            onChange={(e) => setCurrency(e.target.value)}
            className={cn(
              "h-9 px-3 rounded-lg border border-border bg-surface-2 text-[13.5px]",
              "focus:bg-surface focus:border-accent focus:ring-2 focus:ring-accent/14 focus:outline-none",
            )}
          >
            <option value="">{t("currency_select_placeholder")}</option>
            {COMMON_CURRENCIES.map((c) => (
              <option key={c} value={c}>{c}</option>
            ))}
          </select>
        </div>
      </div>
      <FieldEditHistory entityId={entityId} field={amountKey} />
      <div className="flex items-center gap-2">
        <SaveButtonWithHint
          isPending={mutation.isPending}
          isDirty={isDirty}
          missingComment={false}
          onClick={() => mutation.mutate()}
          saveLabel={t("save_button")}
        />
        <Button variant="secondary" onClick={() => setEditing(false)}>
          <X className="h-3.5 w-3.5" aria-hidden /> {t("cancel_button")}
        </Button>
      </div>
    </div>
  );
}

// ─── Typed date field ─────────────────────────────────────────────────────────

type TypedDateKey = "incorporationDateTyped" | "eorObtainmentDateTyped" | "leaseExpiryTyped" | "durationEnd" | "agmDate";

interface TypedDateFieldProps {
  fieldKey: TypedDateKey;
  entity: Entity;
  canEdit: boolean;
  label: string;
  entityId: string;
}

function TypedDateField({
  fieldKey,
  entity,
  canEdit,
  label,
  entityId,
}: TypedDateFieldProps): JSX.Element {
  const { t } = useTranslation("typed_fields");
  const { t: tAudit } = useTranslation("audit");
  const qc = useQueryClient();
  const hasHistory = useFieldHasHistory(fieldKey);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState<string | null>(entity[fieldKey] ?? null);
  const [initialDraft, setInitialDraft] = useState<string | null>(null);

  const value: string | null = entity[fieldKey] ?? null;

  const isDirty = useMemo(() => draft !== initialDraft, [draft, initialDraft]);

  const mutation = useMutation({
    mutationFn: () => {
      const payload: TypedEntityFields = {
        [fieldKey]: draft || null,
        comment: null,
      };
      return patchTypedFields(entityId, payload);
    },
    onSuccess: () => {
      toast.success(t("save_success"));
      void qc.invalidateQueries({ queryKey: ["entity", entityId] });
      void qc.invalidateQueries({ queryKey: ["changelog-recent"] });
      if (ANCHOR_FIELDS.has(fieldKey)) {
        void qc.invalidateQueries({ queryKey: ["entity-obligations", entityId] });
        void qc.invalidateQueries({ queryKey: ["entity-renewal-status", entityId] });
        void qc.invalidateQueries({ queryKey: ["renewals"] });
      }
      setEditing(false);
    },
    onError: (err) => toast.error(extractApiError(err) || t("save_error")),
  });

  if (!editing) {
    return (
      <FieldRow>
        <FieldLabel>{label}</FieldLabel>
        <div className="flex items-center gap-2 flex-1 min-w-0">
          <span className={cn("text-[14px]", value ? "font-normal text-text" : "font-normal text-dim")}>
            {value ?? "—"}
          </span>
          <FieldActions
            fieldLabel={label}
            canEdit={canEdit}
            showClock={hasHistory}
            clockTrigger={hasHistory ? (
              <FieldHistoryPopover
                entityId={entityId}
                field={fieldKey}
                triggerAriaLabel={tAudit("open_field_history", { field: label })}
              />
            ) : undefined}
            onEdit={() => {
              const v = value ?? null;
              setDraft(v);
              setInitialDraft(v);
              setEditing(true);
            }}
            className="ml-auto"
          />
        </div>
      </FieldRow>
    );
  }

  return (
    <div className="py-3 border-b border-border-2 last:border-b-0 space-y-3">
      <FieldLabel>{label}</FieldLabel>
      <DatePickerInput
        value={draft}
        onChange={setDraft}
        placeholder={t("no_typed_date")}
        ariaLabel={label}
      />
      <FieldEditHistory entityId={entityId} field={fieldKey} />
      <div className="flex items-center gap-2">
        <SaveButtonWithHint
          isPending={mutation.isPending}
          isDirty={isDirty}
          missingComment={false}
          onClick={() => mutation.mutate()}
          saveLabel={t("save_button")}
        />
        <Button variant="secondary" onClick={() => setEditing(false)}>
          <X className="h-3.5 w-3.5" aria-hidden /> {t("cancel_button")}
        </Button>
      </div>
    </div>
  );
}

// ─── FiscalYearEnd field (month + day) ───────────────────────────────────────

interface FiscalYearEndFieldProps {
  entity: Entity;
  canEdit: boolean;
  entityId: string;
}

function FiscalYearEndField({ entity, canEdit, entityId }: FiscalYearEndFieldProps): JSX.Element {
  const { t } = useTranslation("typed_fields");
  const { t: tAudit } = useTranslation("audit");
  const qc = useQueryClient();
  const uid = useId();
  const hasHistoryMonth = useFieldHasHistory("fiscalYearEndMonth");
  const hasHistoryDay = useFieldHasHistory("fiscalYearEndDay");
  const hasHistory = hasHistoryMonth || hasHistoryDay;
  const [editing, setEditing] = useState(false);
  const [month, setMonth] = useState<string>(String(entity.fiscalYearEndMonth ?? ""));
  const [day, setDay] = useState<string>(String(entity.fiscalYearEndDay ?? ""));
  const [initialMonth, setInitialMonth] = useState<string>("");
  const [initialDay, setInitialDay] = useState<string>("");

  const monthId = `${uid}-month`;
  const dayId = `${uid}-day`;

  const fyIsDirty = useMemo(
    () => month !== initialMonth || day !== initialDay,
    [month, day, initialMonth, initialDay],
  );

  const MONTH_KEYS = MONTHS;
  const monthLabel = useMemo(() => {
    if (!entity.fiscalYearEndMonth) return null;
    const key = MONTH_KEYS[entity.fiscalYearEndMonth - 1];
    return key ? t(key) : null;
  }, [entity.fiscalYearEndMonth, t, MONTH_KEYS]);

  const displayStr =
    entity.fiscalYearEndMonth && entity.fiscalYearEndDay
      ? `${monthLabel} ${entity.fiscalYearEndDay}`
      : null;

  const mutation = useMutation({
    mutationFn: () =>
      patchTypedFields(entityId, {
        fiscalYearEndMonth: month ? parseInt(month, 10) : null,
        fiscalYearEndDay: day ? parseInt(day, 10) : null,
        comment: null,
      }),
    onSuccess: () => {
      toast.success(t("save_success"));
      void qc.invalidateQueries({ queryKey: ["entity", entityId] });
      void qc.invalidateQueries({ queryKey: ["changelog-recent"] });
      void qc.invalidateQueries({ queryKey: ["entity-obligations", entityId] });
      void qc.invalidateQueries({ queryKey: ["entity-renewal-status", entityId] });
      void qc.invalidateQueries({ queryKey: ["renewals"] });
      setEditing(false);
    },
    onError: (err) => toast.error(extractApiError(err) || t("save_error")),
  });

  if (!editing) {
    return (
      <FieldRow>
        <FieldLabel><Term term="fye">{t("fiscal_year_end")}</Term></FieldLabel>
        <div className="flex items-center gap-2 flex-1 min-w-0">
          {displayStr ? (
            <Tooltip content={t("fiscal_year_end_tooltip")}>
              <span className="text-[14px] font-normal text-text cursor-help">
                {displayStr} <span className="text-dim text-[12px]">({t("fiscal_year_end_every_year")})</span>
              </span>
            </Tooltip>
          ) : (
            <span className="text-[14px] font-normal text-dim">—</span>
          )}
          <FieldActions
            fieldLabel={t("edit_fiscal_year_end")}
            canEdit={canEdit}
            showClock={hasHistory}
            clockTrigger={hasHistory ? (
              <FieldHistoryPopover
                entityId={entityId}
                field="fiscalYearEndMonth"
                triggerAriaLabel={tAudit("open_field_history", { field: t("fiscal_year_end") })}
              />
            ) : undefined}
            onEdit={() => {
              const m = String(entity.fiscalYearEndMonth ?? "");
              const d = String(entity.fiscalYearEndDay ?? "");
              setMonth(m);
              setDay(d);
              setInitialMonth(m);
              setInitialDay(d);
              setEditing(true);
            }}
            className="ml-auto"
          />
        </div>
      </FieldRow>
    );
  }

  return (
    <div className="py-3 border-b border-border-2 last:border-b-0 space-y-3">
      <FieldLabel>{t("fiscal_year_end")}</FieldLabel>
      <div className="flex items-center gap-3 flex-wrap">
        <div>
          <label htmlFor={monthId} className="block text-[10px] font-bold tracking-[.05em] uppercase text-dim mb-1.5">{t("fiscal_month_label")}</label>
          <select
            id={monthId}
            value={month}
            onChange={(e) => setMonth(e.target.value)}
            className={cn(
              "h-9 px-3 rounded-lg border border-border bg-surface-2 text-[13.5px]",
              "focus:bg-surface focus:border-accent focus:ring-2 focus:ring-accent/14 focus:outline-none",
            )}
          >
            <option value="">—</option>
            {MONTHS.map((mk, idx) => (
              <option key={mk} value={idx + 1}>{t(mk)}</option>
            ))}
          </select>
        </div>
        <div>
          <label htmlFor={dayId} className="block text-[10px] font-bold tracking-[.05em] uppercase text-dim mb-1.5">{t("fiscal_day_label")}</label>
          <input
            id={dayId}
            type="number"
            min={1}
            max={31}
            value={day}
            onChange={(e) => setDay(e.target.value)}
            placeholder="DD"
            className={cn(
              "h-9 w-20 px-3 rounded-lg border border-border bg-surface-2 text-[13.5px]",
              "focus:bg-surface focus:border-accent focus:ring-2 focus:ring-accent/14 focus:outline-none",
            )}
          />
        </div>
      </div>
      {/* Show history for the month column — most meaningful anchor for FYE */}
      <FieldEditHistory entityId={entityId} field="fiscalYearEndMonth" />
      <div className="flex items-center gap-2">
        <SaveButtonWithHint
          isPending={mutation.isPending}
          isDirty={fyIsDirty}
          missingComment={false}
          onClick={() => mutation.mutate()}
          saveLabel={t("save_button")}
        />
        <Button variant="secondary" onClick={() => setEditing(false)}>
          <X className="h-3.5 w-3.5" aria-hidden /> {t("cancel_button")}
        </Button>
      </div>
    </div>
  );
}

// ─── Duration field (end date + indefinite flag) ──────────────────────────────

interface DurationFieldProps {
  entity: Entity;
  canEdit: boolean;
  entityId: string;
}

function DurationField({ entity, canEdit, entityId }: DurationFieldProps): JSX.Element {
  const { t } = useTranslation("typed_fields");
  const { t: tAudit } = useTranslation("audit");
  const qc = useQueryClient();
  const uid = useId();
  const hasHistory = useFieldHasHistory("durationEnd");
  const [editing, setEditing] = useState(false);
  const [durationEnd, setDurationEnd] = useState<string | null>(entity.durationEnd ?? null);
  const [indefinite, setIndefinite] = useState<boolean>(entity.durationIndefinite ?? false);
  const [initialEnd, setInitialEnd] = useState<string | null>(null);
  const [initialIndefinite, setInitialIndefinite] = useState<boolean>(false);

  const indefiniteId = `${uid}-indefinite`;

  const durIsDirty = useMemo(
    () => durationEnd !== initialEnd || indefinite !== initialIndefinite,
    [durationEnd, indefinite, initialEnd, initialIndefinite],
  );

  const display = entity.durationIndefinite
    ? t("duration_indefinite")
    : entity.durationEnd ?? null;

  const mutation = useMutation({
    mutationFn: () =>
      patchTypedFields(entityId, {
        durationEnd: indefinite ? null : durationEnd,
        durationIndefinite: indefinite,
        comment: null,
      }),
    onSuccess: () => {
      toast.success(t("save_success"));
      void qc.invalidateQueries({ queryKey: ["entity", entityId] });
      void qc.invalidateQueries({ queryKey: ["changelog-recent"] });
      void qc.invalidateQueries({ queryKey: ["entities"] });
      void qc.invalidateQueries({ queryKey: ["renewals"] });
      setEditing(false);
    },
    onError: (err) => toast.error(extractApiError(err) || t("save_error")),
  });

  if (!editing) {
    return (
      <FieldRow>
        <FieldLabel>{t("duration_end")}</FieldLabel>
        <div className="flex items-center gap-2 flex-1 min-w-0">
          <span className={cn("text-[14px]", display ? "font-normal text-text" : "font-normal text-dim")}>
            {display ?? "—"}
          </span>
          <FieldActions
            fieldLabel={t("edit_duration")}
            canEdit={canEdit}
            showClock={hasHistory}
            clockTrigger={hasHistory ? (
              <FieldHistoryPopover
                entityId={entityId}
                field="durationEnd"
                triggerAriaLabel={tAudit("open_field_history", { field: t("duration_end") })}
              />
            ) : undefined}
            onEdit={() => {
              const e = entity.durationEnd ?? null;
              const i = entity.durationIndefinite ?? false;
              setDurationEnd(e);
              setIndefinite(i);
              setInitialEnd(e);
              setInitialIndefinite(i);
              setEditing(true);
            }}
            className="ml-auto"
          />
        </div>
      </FieldRow>
    );
  }

  return (
    <div className="py-3 border-b border-border-2 last:border-b-0 space-y-3">
      <FieldLabel>{t("duration_end")}</FieldLabel>
      <label htmlFor={indefiniteId} className="flex items-center gap-2 cursor-pointer select-none">
        <input
          id={indefiniteId}
          type="checkbox"
          checked={indefinite}
          onChange={(e) => setIndefinite(e.target.checked)}
          className="h-4 w-4 rounded border-border text-accent focus:ring-accent/40"
        />
        <span className="text-[13.5px] text-text">{t("duration_indefinite")}</span>
      </label>
      {indefinite && (
        <p className="text-[11.5px] text-dim mt-1">{t("duration_indefinite_helper")}</p>
      )}
      {!indefinite && (
        <DatePickerInput
          value={durationEnd}
          onChange={setDurationEnd}
          placeholder={t("no_typed_date")}
        />
      )}
      <FieldEditHistory entityId={entityId} field="durationEnd" />
      <div className="flex items-center gap-2">
        <SaveButtonWithHint
          isPending={mutation.isPending}
          isDirty={durIsDirty}
          missingComment={false}
          onClick={() => mutation.mutate()}
          saveLabel={t("save_button")}
        />
        <Button variant="secondary" onClick={() => setEditing(false)}>
          <X className="h-3.5 w-3.5" aria-hidden /> {t("cancel_button")}
        </Button>
      </div>
    </div>
  );
}

// ─── Main component ───────────────────────────────────────────────────────────

interface TypedFieldsEditorProps {
  entity: Entity;
  canEdit: boolean;
  /**
   * Controls which fields are rendered:
   *  - "all"   (default) — all 8 fields (money + dates); keeps existing callers unchanged.
   *  - "money" — only the two MoneyField rows (shareCapital, parValue).
   *  - "dates" — only the date/FYE/duration rows (items 3–8).
   */
  section?: "money" | "dates" | "all";
}

export function TypedFieldsEditor({ entity, canEdit, section = "all" }: TypedFieldsEditorProps): JSX.Element {
  const { t } = useTranslation("typed_fields");

  const showMoney = section === "all" || section === "money";
  const showDates = section === "all" || section === "dates";

  return (
    <div className="divide-y divide-border-2">
      {showMoney && (
        <>
          <MoneyField
            amountKey="shareCapitalAmount"
            currencyKey="shareCapitalCurrency"
            entity={entity}
            canEdit={canEdit}
            label={t("share_capital_amount")}
            amountLabel={t("share_capital_amount")}
            currencyLabel={t("share_capital_currency")}
            entityId={entity.id}
          />
          <MoneyField
            amountKey="parValueAmount"
            currencyKey="parValueCurrency"
            entity={entity}
            canEdit={canEdit}
            label={t("par_value_amount")}
            amountLabel={t("par_value_amount")}
            currencyLabel={t("par_value_currency")}
            entityId={entity.id}
          />
        </>
      )}
      {showDates && (
        <>
          <TypedDateField
            fieldKey="incorporationDateTyped"
            entity={entity}
            canEdit={canEdit}
            label={t("incorporation_date")}
            entityId={entity.id}
          />
          <TypedDateField
            fieldKey="eorObtainmentDateTyped"
            entity={entity}
            canEdit={canEdit}
            label={t("eor_obtainment_date")}
            entityId={entity.id}
          />
          <TypedDateField
            fieldKey="leaseExpiryTyped"
            entity={entity}
            canEdit={canEdit}
            label={t("lease_expiry")}
            entityId={entity.id}
          />
          <TypedDateField
            fieldKey="agmDate"
            entity={entity}
            canEdit={canEdit}
            label={t("agm_date")}
            entityId={entity.id}
          />
          <FiscalYearEndField entity={entity} canEdit={canEdit} entityId={entity.id} />
          <DurationField entity={entity} canEdit={canEdit} entityId={entity.id} />
        </>
      )}
    </div>
  );
}
